<template>
  <div class="refine" :class="{ 'refine--compact': compact }">
    <div ref="scroll" class="refine__messages">
      <p v-if="!messages.length && !sending" class="refine__intro">
        Ask Alice to sharpen this insight, back it up with quotes, shorten it or
        translate it. She checks against the project data and proposes a revised
        insight you apply with one click.
      </p>

      <div
        v-for="(m, i) in messages"
        :key="i"
        class="refine__msg"
        :class="`refine__msg--${m.role}`"
      >
        <div class="refine__role">
          {{ m.role === "user" ? "You" : "Alice" }}
        </div>
        <div v-if="m.role === 'user' || m.display" class="refine__body">
          {{ m.role === "user" ? m.content : m.display }}
        </div>
        <div v-if="m.proposal" class="refine__proposal">
          <div class="refine__proposal-head">
            <span class="refine__proposal-label">Proposal</span>
            <span class="refine__proposal-meta">{{ proposalMeta(m) }}</span>
          </div>

          <div class="refine__proposal-title">
            {{ m.proposal.title }}
            <span v-if="m.diff.titleChanged" class="refine__tag">edited</span>
          </div>
          <div
            v-if="m.diff.titleChanged && m.diff.oldTitle"
            class="refine__proposal-row refine__proposal-row--removed"
          >
            {{ m.diff.oldTitle }}
          </div>

          <div
            v-if="m.diff.findings.length || m.diff.removedFindings.length"
            class="refine__proposal-group"
          >
            <div class="refine__proposal-group-label">observaties</div>
            <div
              v-for="(f, fi) in m.diff.findings"
              :key="`f${fi}`"
              class="refine__proposal-row"
            >
              {{ f.text }}
              <span v-if="f.isNew" class="refine__tag">new</span>
            </div>
            <div
              v-for="(text, fi) in m.diff.removedFindings"
              :key="`fr${fi}`"
              class="refine__proposal-row refine__proposal-row--removed"
            >
              {{ text }}
            </div>
          </div>

          <div
            v-if="m.diff.quotes.length || m.diff.removedQuotes.length"
            class="refine__proposal-group"
          >
            <div class="refine__proposal-group-label">quotes</div>
            <div
              v-for="(q, qi) in m.diff.quotes"
              :key="`q${qi}`"
              class="refine__proposal-row refine__proposal-row--quote"
              :class="{ 'refine__proposal-row--muted': !q.selected }"
            >
              <span class="refine__quote-text">{{ q.text }}</span>
              <span v-if="q.isNew" class="refine__tag">new</span>
              <span v-if="quoteMeta(q)" class="refine__quote-name">{{
                quoteMeta(q)
              }}</span>
            </div>
            <div
              v-for="(q, qi) in m.diff.removedQuotes"
              :key="`qr${qi}`"
              class="refine__proposal-row refine__proposal-row--quote refine__proposal-row--removed"
            >
              <span class="refine__quote-text">{{ q.text }}</span>
              <span v-if="q.name" class="refine__quote-name">{{ q.name }}</span>
            </div>
          </div>

          <button
            type="button"
            class="refine__apply"
            :disabled="m.applied"
            @click="apply(m)"
          >
            {{ m.applied ? "Applied" : "Apply" }}
          </button>
        </div>
        <div v-if="m.fallbackModel" class="refine__note">
          Answered with {{ m.fallbackModel }} — Claude ran out of room for this
          project's data.
        </div>
      </div>

      <div v-if="sending" class="refine__msg refine__msg--assistant">
        <div class="refine__role">Alice</div>
        <ThinkingDots :size="5" />
      </div>
    </div>

    <div v-if="error" class="refine__error">{{ error }}</div>

    <form class="refine__input" @submit.prevent="send">
      <textarea
        v-model="input"
        class="refine__textarea"
        rows="2"
        placeholder="e.g. “Make the title sharper” or “Add a quote that supports this”"
        :disabled="sending"
        @keydown.enter.exact.prevent="send"
      />
      <button
        type="submit"
        class="refine__send"
        :disabled="sending || !input.trim()"
        title="Send"
      >
        <Icon name="arrow-right" :size="14" />
      </button>
    </form>
  </div>
</template>

<script>
import { httpsCallable } from "firebase/functions";
import { functions } from "@/firebase";
import Icon from "@/components/alice/Icon.vue";
import ThinkingDots from "@/components/alice/ThinkingDots.vue";

function rowText(row) {
  if (row === null || row === undefined) return "";
  return typeof row === "string" ? row : String(row.text || "");
}

// Only stored fields go to the model — no `_open`, no `order`.
function pickInsightFields(insight) {
  return {
    uid: insight.uid || null,
    topic: String(insight.topic || ""),
    title: String(insight.title || ""),
    findings: (insight.findings || []).map((f) => ({ text: rowText(f) })),
    quotes: (insight.quotes || []).map((q) => {
      const row = { text: rowText(q), selected: !!(q && q.selected) };
      if (q && q.name) row.name = String(q.name);
      return row;
    }),
    strength: typeof insight.strength === "number" ? insight.strength : null,
  };
}

// Loose text identity for diffing rows: case, whitespace and curly vs
// straight quotes don't make a row "new".
function sameKey(text) {
  return String(text || "")
    .toLowerCase()
    .replace(/[\u2018\u2019\u201c\u201d"']/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

// What the proposal changes relative to the insight it was asked about.
// Apply replaces every row, so rows that would disappear are listed too.
function diffProposal(base, proposal) {
  const baseFindings = new Set(base.findings.map((f) => sameKey(f.text)));
  const baseQuotes = new Set(base.quotes.map((q) => sameKey(q.text)));
  const findings = (proposal.findings || []).map((f) => ({
    text: rowText(f),
    isNew: !baseFindings.has(sameKey(rowText(f))),
  }));
  const quotes = (proposal.quotes || []).map((q) => ({
    text: rowText(q),
    name: (q && q.name) || "",
    selected: !!(q && q.selected),
    isNew: !baseQuotes.has(sameKey(rowText(q))),
  }));
  const keptFindings = new Set(findings.map((f) => sameKey(f.text)));
  const keptQuotes = new Set(quotes.map((q) => sameKey(q.text)));
  const titleChanged =
    sameKey(proposal.title) !== sameKey(base.title) && !!base.title;
  return {
    titleChanged,
    oldTitle: titleChanged ? base.title : "",
    findings,
    removedFindings: base.findings
      .map((f) => f.text)
      .filter((t) => t.trim() && !keptFindings.has(sameKey(t))),
    quotes,
    removedQuotes: base.quotes.filter(
      (q) => q.text.trim() && !keptQuotes.has(sameKey(q.text))
    ),
    strengthFrom: base.strength,
  };
}

// Local-only chat that refines one insight via the refineInsight callable.
// The parent keys this component by insight uid so the transcript resets
// when another insight is opened. `apply` hands the normalised proposal up;
// the parent decides how to write it (in memory on the Insights page, onto
// the dialog draft in the chat aside).
export default {
  name: "InsightRefineChat",
  components: { Icon, ThinkingDots },
  props: {
    insight: { type: Object, required: true },
    projectUid: { type: String, required: true },
    chapterUid: { type: String, default: null },
    model: { type: String, default: "claude-opus-5-5" },
    compact: { type: Boolean, default: false },
  },
  emits: ["apply"],
  data() {
    return {
      messages: [],
      input: "",
      sending: false,
      error: "",
    };
  },
  methods: {
    quoteMeta(q) {
      return [q.name, q.selected ? "" : "niet in rapport"]
        .filter(Boolean)
        .join(" · ");
    },
    proposalMeta(m) {
      const p = m.proposal;
      const parts = [
        `${(p.findings || []).length} finding${
          (p.findings || []).length === 1 ? "" : "s"
        }`,
        `${(p.quotes || []).length} quote${
          (p.quotes || []).length === 1 ? "" : "s"
        }`,
      ];
      if (typeof p.strength === "number") {
        const from = m.diff.strengthFrom;
        parts.push(
          typeof from === "number" && from !== p.strength
            ? `strength ${from} → ${p.strength}`
            : `strength ${p.strength}`
        );
      }
      return parts.join(" · ");
    },
    async send() {
      const text = this.input.trim();
      if (!text || this.sending) return;
      this.error = "";
      this.sending = true;
      this.messages.push({ role: "user", content: text });
      this.input = "";
      this.scrollDown();
      // Snapshot what was asked about, so the proposal diff stays stable
      // while the slide is edited or another proposal is applied.
      const base = pickInsightFields(this.insight);
      let answered = false;
      try {
        const call = httpsCallable(functions, "refineInsight", {
          timeout: 300000,
        });
        const res = await call({
          projectUid: this.projectUid,
          chapterUid: this.chapterUid,
          insight: base,
          // `content` of an assistant turn is the raw reply (json block
          // included) so the model sees its own earlier proposal.
          messages: this.messages.map(({ role, content }) => ({
            role,
            content,
          })),
          model: this.model,
        });
        const d = res.data || {};
        this.messages.push({
          role: "assistant",
          content: d.raw || d.text || "",
          display: d.text || "",
          proposal: d.proposal || null,
          diff: d.proposal ? diffProposal(base, d.proposal) : null,
          fallbackModel: d.fallbackModel || null,
          applied: false,
        });
        answered = true;
      } catch (e) {
        this.error = this.describeError(e);
      } finally {
        this.sending = false;
        // A proposal can be long: show the reply from its first line
        // rather than jumping to the bottom.
        if (answered) this.scrollToLastMessage();
        else this.scrollDown();
      }
    },
    describeError(e) {
      const code = (e && e.code) || "";
      if (code.endsWith("permission-denied")) {
        return "Not available for guest accounts.";
      }
      if (code.endsWith("resource-exhausted")) {
        return "The project data is too large for this model.";
      }
      if (code.endsWith("deadline-exceeded")) {
        return "Alice took too long — please try again.";
      }
      return (e && e.message) || "Something went wrong. Please try again.";
    },
    apply(m) {
      if (!m.proposal || m.applied) return;
      this.$emit("apply", m.proposal);
      m.applied = true;
    },
    scrollDown() {
      this.$nextTick(() => {
        const el = this.$refs.scroll;
        if (el) el.scrollTop = el.scrollHeight;
      });
    },
    scrollToLastMessage() {
      this.$nextTick(() => {
        const el = this.$refs.scroll;
        const msgs = el ? el.querySelectorAll(".refine__msg") : [];
        const last = msgs[msgs.length - 1];
        if (!last) return;
        el.scrollTop +=
          last.getBoundingClientRect().top -
          el.getBoundingClientRect().top -
          14;
      });
    },
  },
};
</script>

<style scoped lang="scss">
.refine {
  display: flex;
  flex-direction: column;
  gap: 10px;
  border: 1px solid var(--border-hairline);
  background: var(--bg-muted);
}
.refine__messages {
  display: flex;
  flex-direction: column;
  gap: 14px;
  max-height: 360px;
  overflow-y: auto;
  padding: 14px 16px 4px;
}
/* Inside the slide drawer the chat sits in a bordered bar already. */
.refine--compact {
  border: none;
  border-top: 1px solid var(--border-hairline);
}
.refine--compact .refine__messages {
  max-height: min(560px, 60vh);
}
.refine__intro {
  margin: 0;
  font-family: var(--font-body);
  font-size: 13px;
  line-height: 1.55;
  color: var(--fg-3);
}
.refine__role {
  margin-bottom: 4px;
  font-family: var(--font-ui);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--fg-3);
}
.refine__body {
  font-family: var(--font-body);
  font-size: 14px;
  line-height: 1.55;
  color: var(--vp-black);
  white-space: pre-wrap;
  word-break: break-word;
}
.refine__msg--user .refine__body {
  padding: 8px 10px;
  background: var(--vp-white);
  border: 1px solid var(--border-hairline);
}
.refine__proposal {
  margin-top: 8px;
  padding: 10px 12px;
  background: var(--vp-white);
  border: 1px solid var(--vp-black);
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.refine__proposal-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}
.refine__proposal-label {
  font-family: var(--font-ui);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--vp-black);
}
.refine__proposal-meta {
  font-family: var(--font-ui);
  font-size: 11px;
  color: var(--fg-3);
}
.refine__proposal-title {
  font-family: var(--font-body);
  font-size: 15px;
  font-weight: 700;
  line-height: 1.4;
  color: var(--vp-black);
}
.refine__proposal-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: 6px;
}
.refine__proposal-group-label {
  font-family: var(--font-ui);
  font-size: 11px;
  letter-spacing: 0.06em;
  color: var(--fg-3);
}
.refine__proposal-row {
  padding-left: 10px;
  border-left: 2px solid var(--border-hairline);
  font-family: var(--font-body);
  font-size: 14px;
  line-height: 1.5;
  color: var(--vp-black);
  word-break: break-word;
}
.refine__proposal-row--quote .refine__quote-text {
  font-family: var(--font-display);
  font-style: italic;
  font-size: 15px;
}
.refine__quote-name {
  display: block;
  margin-top: 2px;
  font-family: var(--font-ui);
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--fg-3);
}
.refine__proposal-row--muted .refine__quote-text {
  color: var(--fg-3);
}
.refine__proposal-row--removed {
  color: var(--fg-3);
  text-decoration: line-through;
  text-decoration-color: var(--vp-grijs-80);
}
.refine__proposal-row--removed .refine__quote-text {
  color: var(--fg-3);
}
.refine__tag {
  display: inline-block;
  margin-left: 6px;
  padding: 1px 6px;
  vertical-align: 2px;
  background: var(--accent-20);
  font-family: var(--font-ui);
  font-size: 10px;
  font-style: normal;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--vp-black);
}
.refine__proposal .refine__apply {
  margin-top: 6px;
}
.refine__apply {
  align-self: flex-start;
  padding: 6px 14px;
  background: var(--vp-black);
  color: var(--vp-white);
  border: 1px solid var(--vp-black);
  border-radius: 0;
  cursor: pointer;
  font-family: var(--font-ui);
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  transition: background 0.12s ease, color 0.12s ease;
}
.refine__apply:hover:not(:disabled) {
  background: var(--accent-100);
  color: var(--vp-black);
}
.refine__apply:disabled {
  background: var(--vp-white);
  color: var(--fg-3);
  border-color: var(--border-hairline);
  cursor: default;
}
.refine__note {
  margin-top: 6px;
  font-family: var(--font-ui);
  font-size: 12px;
  color: var(--fg-3);
}
.refine__error {
  padding: 0 16px;
  font-family: var(--font-ui);
  font-size: 12px;
  color: var(--vp-rood-100);
}
.refine__input {
  display: flex;
  gap: 8px;
  padding: 0 12px 12px;
}
.refine__textarea {
  flex: 1;
  resize: none;
  padding: 8px 10px;
  border: 1px solid var(--border-hairline);
  outline: none;
  background: var(--vp-white);
  font-family: var(--font-body);
  font-size: 13px;
  line-height: 1.5;
  color: var(--vp-black);
}
.refine__textarea:focus {
  border-color: var(--vp-black);
}
.refine__send {
  flex-shrink: 0;
  width: 38px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: var(--vp-black);
  color: var(--vp-white);
  border: none;
  border-radius: 0;
  cursor: pointer;
}
.refine__send:disabled {
  opacity: 0.4;
  cursor: default;
}
</style>
